import React from 'react';
import { Box, List, ListItemText, useTheme } from '@mui/material';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { Question, QuestionOption } from '../../types';
import { DragIndicator as DragIcon } from '@mui/icons-material';

const ITEM_HEIGHT_MULTIPLIER = 7; // This will be multiplied by theme.spacing(1)

interface Props {
  question: Question;
  selectedOptions?: number[];
  onChange: (orderedOptionIds: number[]) => void;
  onSubmit?: (response: { selected_options: number[] }) => void;
}

export const DragAndDropQuestion: React.FC<Props> = ({
  question,
  selectedOptions = [],
  onChange,
  onSubmit,
}) => {
  const theme = useTheme();
  const itemHeight = theme.spacing(ITEM_HEIGHT_MULTIPLIER);

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(selectedOptions);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    onChange(items);
  };

  const handleSubmit = () => {
    if (onSubmit) {
      onSubmit({
        selected_options: selectedOptions // This will preserve the order
      });
    }
  };

  React.useEffect(() => {
    if (selectedOptions.length === 0 && question.options && question.options.length > 0) {
      const sortedOptions = [...question.options].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      onChange(sortedOptions.map(opt => opt.id));
    }
  }, [question.options, selectedOptions, onChange]);

  if (!question.options || question.options.length === 0) return null;

  const orderedOptions = selectedOptions
    .map(id => question.options?.find(opt => opt.id === id))
    .filter((opt): opt is QuestionOption => opt !== undefined);

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable droppableId="droppable-options">
        {(provided, snapshot) => (
          <List 
            {...provided.droppableProps} 
            ref={provided.innerRef}
            sx={{
              position: 'static !important',
              transform: 'none !important',
              '& > *': { position: 'static !important' },
              height: `calc(${orderedOptions.length + 2} * ${itemHeight})`,
              overflow: 'hidden'
            }}
          >
            {orderedOptions.map((option, index) => (
              <Draggable key={option.id} draggableId={String(option.id)} index={index}>
                {(provided, snapshot) => (
                  <Box
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    sx={{
                      mb: 1,
                      '&:last-child': {
                        mb: 0
                      }
                    }}
                  >
                    <Box
                      {...provided.dragHandleProps}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        p: 2,
                        border: '1px solid rgba(255, 255, 255, 0.12)',
                        borderRadius: 1,
                        bgcolor: snapshot.isDragging ? 'action.hover' : 'background.paper',
                        transition: 'background-color 0.2s ease',
                        cursor: 'grab',
                        '&:hover': {
                          bgcolor: 'action.hover'
                        },
                        '&:active': {
                          cursor: 'grabbing'
                        }
                      }}
                    >
                      <DragIcon sx={{ mr: 2, color: 'text.secondary' }} />
                      <ListItemText primary={option.front_text} />
                    </Box>
                  </Box>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </List>
        )}
      </Droppable>
    </DragDropContext>
  );
};
